import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Input, Checkbox, Card, Space, Table, message } from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';
import moment from 'moment';
import { FaWater } from 'react-icons/fa';
import CustomDatePicker from '../Shared/CustomDatePicker';

const ProductPopUp = ({ visible, onCancel, onSubmit, initialValues = null }) => {
    const [form] = Form.useForm();
    const [isSoil, setIsSoil] = useState(false);
    const [isIrrigation, setIsIrrigation] = useState(false);
    const [maxValves, setMaxValves] = useState(0);
    const [maxTanks, setMaxTanks] = useState(0);
    const [activeSensors, setActiveSensors] = useState({
        level1: [],
        level2: []
    });
    const [sensorError, setSensorError] = useState('');

    useEffect(() => {
        if (visible) {
            if (initialValues) {
                form.setFieldsValue({
                    ...initialValues,
                    creation_date: initialValues.creation_date ? moment(initialValues.creation_date) : null,
                });
                setActiveSensors({
                    level1: initialValues.level1_type_sensors.split(",") || [],
                    level2: initialValues.level2_type_sensors.split(",") || []
                });
                setIsSoil(initialValues.is_soil || false);

                setIsIrrigation(initialValues.is_irrigation || false);
                setMaxValves(initialValues.max_valves || 0);
                setMaxTanks(initialValues.max_tanks || 0);
            } else {
                form.resetFields();
                setActiveSensors({ level1: [], level2: [] });
                setIsSoil(false);

                setIsIrrigation(false);
                setMaxValves(0);
                setMaxTanks(0);
            }
            checkSensorRequirements();
        }
    }, [visible, form, initialValues]);

    const checkSensorRequirements = useCallback(() => {
        if (isSoil) {
            const level1Count = activeSensors.level1.length;
            const level2Count = activeSensors.level2.length;
            if (level1Count < 3 || level2Count < 2) {
                setSensorError(`Veuillez sélectionner au moins 3 capteurs de niveau 1 (actuellement ${level1Count}) et 2 capteurs de niveau 2 (actuellement ${level2Count}).`);
                return false;
            } else {
                setSensorError('');
                return true;
            }
        }
        return true;
    }, [isSoil, activeSensors]);


    const checkIrrigationRequirements = useCallback(() => {
        if (isIrrigation) {
            if (maxValves < 1 || maxTanks < 1) {
                return false;
            }
        }
        return true;
    }, [isIrrigation, maxValves, maxTanks]);



    useEffect(() => {
        checkSensorRequirements();
    }, [checkSensorRequirements]);

    useEffect(() => {
        checkIrrigationRequirements();
    }, [checkIrrigationRequirements]);

    const handleSubmit = () => {
        if (checkSensorRequirements() && checkIrrigationRequirements()) {
            form.validateFields().then((values) => {
                const formattedValues = {
                    ...values,
                    creation_date: values.creation_date ? values.creation_date.toISOString() : moment().toISOString(),
                    level1_type_sensors: activeSensors.level1.join(','),
                    level2_type_sensors: activeSensors.level2.join(','),
                    max_valves: isIrrigation ? maxValves : null,
                    max_tanks: isIrrigation ? maxTanks : null,
                };
                onSubmit(formattedValues);
            }).catch((info) => {
                console.error('Validate Failed:', info);
            });
        } else {
            message.error('Veuillez remplir les champs avec des valeurs valides');
        }
    };

    const handleSoilChange = (e) => {
        const isChecked = e.target.checked;
        form.setFieldsValue({ is_soil: isChecked });
        setIsSoil(isChecked);
        if (!isChecked) {
            setActiveSensors({ level1: [], level2: [] });
            setSensorError('');
        }
    };

    const handleIrrigationChange = (e) => {
        const isChecked = e.target.checked;
        form.setFieldsValue({ is_irrigation: isChecked });
        setIsIrrigation(isChecked);
    };


    const onValuesChange = (changedValues, allValues) => {
        if (changedValues.max_valves) {
            setMaxValves(changedValues.max_valves);
        }
        if (changedValues.max_tanks) {
            setMaxTanks(changedValues.max_tanks);
        }
    };



    const toggleSensor = (level, sensor) => {
        setActiveSensors(prev => {
            const newSensors = prev[level].includes(sensor)
                ? prev[level].filter(s => s !== sensor)
                : [...prev[level], sensor];
            return { ...prev, [level]: newSensors };
        });
    };

    const level1Options = ['pH', 'EC', 'Hum', 'Temp', 'N', 'P', 'K'];
    const level2Options = ['Temp', 'Hum', 'EC'];

    const columns = [
        {
            title: 'Niveau 1',
            dataIndex: 'level1',
            key: 'level1',
            render: (sensor) => (
                <div
                    className={`cursor-pointer p-2 rounded ${activeSensors.level1.includes(sensor) ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                    onClick={() => toggleSensor('level1', sensor)}
                >
                    {sensor}
                </div>
            ),
        },
        {
            title: 'Niveau 2',
            dataIndex: 'level2',
            key: 'level2',
            render: (sensor) => (
                sensor ? (
                    <div
                        className={`cursor-pointer p-2 rounded ${activeSensors.level2.includes(sensor) ? 'bg-green-500 text-white' : 'bg-gray-200'}`}
                        onClick={() => toggleSensor('level2', sensor)}
                    >
                        {sensor}
                    </div>
                ) : null
            ),
        },
    ];

    const data = level1Options.map((sensor, index) => ({
        key: index,
        level1: sensor,
        level2: level2Options[index] || null,
    }));


    return (
        <Modal
            open={visible}
            title={initialValues ? "Modifier le produit" : "Ajouter un produit"}
            onCancel={onCancel}
            onOk={handleSubmit}
            width={600}
            className="p-4"
        >
            <Form form={form} onValuesChange={onValuesChange} layout="vertical">
                <Form.Item
                    name="product_name"
                    label="Nom du produit"
                    rules={[{ required: true, message: 'Veuillez entrer le nom du produit' }]}
                >
                    <Input className="w-full p-2 border border-gray-300 rounded-md" />
                </Form.Item>

                <Form.Item name="creation_date" label="Date de création">
                    <CustomDatePicker />
                </Form.Item>

                <Form.Item label="Thèmes">
                    <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
                        <Card
                            size="small"
                            title={
                                <span className="flex items-center">
                                    <FaWater className="mr-2" />
                                    Irrigation
                                </span>
                            }
                            className="w-full"
                        >
                            <Form.Item name="is_irrigation" valuePropName="checked" noStyle>
                                <Checkbox onChange={handleIrrigationChange}>Activer l'irrigation</Checkbox>
                            </Form.Item>
                            {isIrrigation && (
                                <div className="mt-4">
                                    <Form.Item
                                        name="max_valves"
                                        label="Nombre de vannes"
                                        rules={[{ required: true, message: 'Veuillez entrer le nombre de vannes' }]}
                                    >
                                        <Input type="number"  className="w-full p-2 border border-gray-300 rounded-md" />
                                    </Form.Item>
                                    <Form.Item
                                        name="max_tanks"
                                        label="Nombre de citernes"
                                        rules={[{ required: true, message: 'Veuillez entrer le nombre de citernes' }]}
                                    >
                                        <Input type="number" className="w-full p-2 border border-gray-300 rounded-md" />
                                    </Form.Item>
                                </div>
                            )}
                        </Card>
                        <Card
                            size="small"
                            title={
                                <span className="flex items-center">
                                    <EnvironmentOutlined className="mr-2" />
                                    Sol
                                </span>
                            }
                            className="w-full"
                        >
                            <Form.Item name="is_soil" valuePropName="checked" noStyle>
                                <Checkbox onChange={handleSoilChange}>Activer le sol</Checkbox>
                            </Form.Item>
                            {isSoil && (
                                <div className="mt-4">
                                    <Table
                                        columns={columns}
                                        dataSource={data}
                                        pagination={false}
                                        size="small"
                                        onRow={() => ({
                                            onClick: (event) => {
                                                // Stop propagation to prevent row click
                                                event.stopPropagation();
                                            },
                                        })}
                                    />
                                    {sensorError && (
                                        <p className="mt-2 text-sm text-red-500">
                                            {sensorError}
                                        </p>
                                    )}
                                </div>
                            )}
                        </Card>
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default ProductPopUp;